import { ReactComponent as AddIcon } from "assets/images/icons/icn_btn_add.svg";

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Divider } from "@mui/material";
import { createColumnHelper } from "@tanstack/react-table";
import { v4 as uuidv4 } from "uuid";

import SearchInput from "components/SearchInput";
import TableField from "components/Table";
import { SolidButton } from "components/buttons/SolidButton";
import useAuthScope from "hooks/useAuthScope";
import useDataSourceManager from "hooks/useDataSourceManager";
import useTableInstance from "hooks/useTableInstance";

import ScheduleModal from "./Modal";
import PreviewPopover from "./PreviewPopover";
import RemoveModal from "./RemoveModal";
import GlobalAlert from "components/Notification";
import { getScheduleTableColumns } from "./util/func";
import { scheduleActionState } from "./util/const";

const dayApiToViewMap = {
  SUNDAY: "sunday",
  MONDAY: "monday",
  TUESDAY: "tuesday",
  WEDNESDAY: "wednesday",
  THURSDAY: "thursday",
  FRIDAY: "friday",
  SATURDAY: "saturday",
};

const dayViewToApiMap = Object.entries(dayApiToViewMap).reduce((result, [apiDay, viewDay]) => ({
  ...result,
  [viewDay]: apiDay,
}), {});

const fromApiScheduleProfile = (profile) => ({
  ...profile,
  days: (profile.days || []).map((day) => ({
    dayOfWeek: dayApiToViewMap[day.day] || day.day,
    enabled: day.availability,
    from: day.startTime,
    to: day.endTime,
  })),
});

const toApiScheduleProfile = (profile) => ({
  name: profile.name,
  displayFormat: profile.displayFormat || "24-hour",
  days: (profile.days || []).map((day) => ({
    day: dayViewToApiMap[day.dayOfWeek] || day.dayOfWeek,
    startTime: day.from,
    endTime: day.to,
    availability: day.enabled,
  })),
});

const getScheduleRequestParams = ({ auth, method = "read", params }) => {
  const isStandaloneMode = !auth.orgs || Object.keys(auth.orgs).length === 0;
  const apiBaseUrl = isStandaloneMode ? "/orgs/:orgId" : "/ents/:entId/orgs/:orgId";
  const hasProfileScheduleId = Boolean(params?.profileScheduleId);
  const inFix = hasProfileScheduleId ? "/:profileScheduleId" : "";
  const apiUrl = `${apiBaseUrl}/sites/:siteId/settings/profileSchedule${inFix}.${method}`;

  const requestParams = isStandaloneMode
    ? {
      orgId: auth.realm?.id,
      siteId: auth.sites?.id,
    }
    : {
      entId: auth.realm?.id,
      orgId: auth.orgs?.id,
      siteId: auth.sites?.id,
    };

  if (hasProfileScheduleId) requestParams.profileScheduleId = params.profileScheduleId;

  return {
    apiUrl,
    requestParams,
  };
};

const ProfileSchedule = ({ showEditGeneral }) => {
  const columnHelper = createColumnHelper();
  const { auth, isAuthReady } = useAuthScope();
  const { dataMap, getData, sendRequest } = useDataSourceManager();
  const closePreviewTimer = useRef(null);
  const previewPopoverRef = useRef(null);

  const [uuids] = useState(() => ({
    read: uuidv4(),
    create: uuidv4(),
    update: uuidv4(),
    remove: uuidv4(),
  }));

  const [isLoading, setIsLoading] = useState(false);
  const [originProfiles, setOriginProfiles] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [searchVal, setSearchVal] = useState("");
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [actionState, setActionState] = useState(scheduleActionState.idle);
  const [alertInfo, setAlertInfo] = useState({
    open: false,
    severity: "",
    message: "",
  });
  const pendingRequestRef = useRef({
    read: false,
    create: false,
    update: false,
    remove: false,
  });
  const [previewAnchorPosition, setPreviewAnchorPosition] = useState(null);
  const [previewProfile, setPreviewProfile] = useState(null);

  const fetchScheduleProfiles = () => {
    if (!isAuthReady) return;

    setIsLoading(true);
    pendingRequestRef.current.read = true;

    const { apiUrl, requestParams } = getScheduleRequestParams({
      auth,
      method: "read",
    });

    sendRequest(
      apiUrl,
      uuids.read,
      requestParams,
    );
  };

  const filterProfiles = (keyword, source = originProfiles) => {
    if (!keyword) return source;

    const normalizedKeyword = keyword.toLowerCase();

    return source.filter((profile) => (
      profile.name.toLowerCase().includes(normalizedKeyword)
    ));
  };

  const handleSearchChange = (value) => {
    setSearchVal(value);
    setProfiles(filterProfiles(value));
  };

  const handleCreate = () => {
    setSelectedProfile(null);
    setActionState(scheduleActionState.create);
  };

  const clearClosePreviewTimer = () => {
    if (closePreviewTimer.current) {
      clearTimeout(closePreviewTimer.current);
      closePreviewTimer.current = null;
    }
  };

  const handlePreview = (profile, anchorEl) => {
    clearClosePreviewTimer();
    const rect = anchorEl.getBoundingClientRect();

    setPreviewProfile(profile);
    setPreviewAnchorPosition({
      top: rect.bottom,
      left: rect.left + (rect.width / 2),
    });
  };

  const closePreview = () => {
    clearClosePreviewTimer();
    setPreviewAnchorPosition(null);
    setPreviewProfile(null);
  };

  const scheduleClosePreview = () => {
    clearClosePreviewTimer();
    closePreviewTimer.current = setTimeout(closePreview, 120);
  };

  const handleEdit = (profile) => {
    setSelectedProfile(profile);
    setActionState(scheduleActionState.edit);
  };

  const handleRemove = (profile) => {
    setSelectedProfile(profile);
    setActionState(scheduleActionState.remove);
  };

  const closeScheduleModal = () => {
    setSelectedProfile(null);
    setActionState(scheduleActionState.idle);
  };

  const handleSubmitSchedule = (payload) => {
    const isCreateMode = actionState === scheduleActionState.create;
    if (isCreateMode) {
      pendingRequestRef.current.create = true;
    } else {
      pendingRequestRef.current.update = true;
    }
    const { apiUrl, requestParams } = getScheduleRequestParams({
      auth,
      method: isCreateMode ? "create" : "update",
      params: { profileScheduleId: payload.id },
    });

    sendRequest(
      apiUrl,
      isCreateMode ? uuids.create : uuids.update,
      requestParams,
      toApiScheduleProfile(payload),
    );
  };

  const handleConfirmRemove = () => {
    if (!selectedProfile?.id) return;

    setIsLoading(true);
    pendingRequestRef.current.remove = true;

    const { apiUrl, requestParams } = getScheduleRequestParams({
      auth,
      method: "delete",
    });

    sendRequest(
      apiUrl,
      uuids.remove,
      requestParams,
      { profileScheduleIds: [selectedProfile.id] },
    );
  };

  const columns = useMemo(() => getScheduleTableColumns({
    columnHelper,
    showEditGeneral,
    onPreview: handlePreview,
    onPreviewLeave: scheduleClosePreview,
    onEdit: handleEdit,
    onRemove: handleRemove,
  }), [columnHelper, showEditGeneral]);

  const table = useTableInstance({
    tableName: "profilesScheduleTable",
    data: profiles,
    columns,
    enableSorting: true,
    enablePagination: true,
    enableGlobalFilter: true,
  });

  useEffect(() => {
    fetchScheduleProfiles();
  }, [isAuthReady]);

  useEffect(() => {
    setProfiles(filterProfiles(searchVal, originProfiles));
  }, [originProfiles]);

  useEffect(() => {
    if (!dataMap?.[uuids.read] || !pendingRequestRef.current.read) return;
    pendingRequestRef.current.read = false;

    const response = getData(uuids.read);
    if (response && response.hasOwnProperty("code") && response?.code !== 200) {
      setAlertInfo({
        open: true,
        severity: "error",
        message: response?.message || "無法取得排程設定檔。",
      });
      setIsLoading(false);
    } else {
      const responseData = (response?.data ?? []).map(fromApiScheduleProfile);
      setOriginProfiles(responseData);
      setProfiles(filterProfiles(searchVal, responseData));
      setIsLoading(false);
    }
  }, [dataMap?.[uuids.read]]);

  useEffect(() => {
    if (!dataMap?.[uuids.create] || !pendingRequestRef.current.create) return;
    pendingRequestRef.current.create = false;

    const response = getData(uuids.create);
    if (response && response.hasOwnProperty("code") && response?.code !== 200) {
      setAlertInfo({
        open: true,
        severity: "error",
        message: response?.message || "建立排程設定檔失敗。",
      });
    } else {
      closeScheduleModal();
      fetchScheduleProfiles();
    }
  }, [dataMap?.[uuids.create]]);

  useEffect(() => {
    if (!dataMap?.[uuids.update] || !pendingRequestRef.current.update) return;
    pendingRequestRef.current.update = false;

    const response = getData(uuids.update);
    if (response && response.hasOwnProperty("code") && response?.code !== 200) {
      setAlertInfo({
        open: true,
        severity: "error",
        message: response?.message || "更新排程設定檔失敗。",
      });
    } else {
      closeScheduleModal();
      fetchScheduleProfiles();
    }
  }, [dataMap?.[uuids.update]]);

  useEffect(() => {
    if (!dataMap?.[uuids.remove] || !pendingRequestRef.current.remove) return;
    pendingRequestRef.current.remove = false;

    const response = getData(uuids.remove);
    if (response && response.hasOwnProperty("code") && response?.code !== 200) {
      setAlertInfo({
        open: true,
        severity: "error",
        message: response?.message || "刪除排程設定檔失敗。",
      });
      setIsLoading(false);
    } else {
      closeScheduleModal();
      fetchScheduleProfiles();
    }
  }, [dataMap?.[uuids.remove]]);

  useEffect(() => () => clearClosePreviewTimer(), []);

  return (
    <Box sx={{ padding: "20px" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          height: "80px",
          gap: 3,
        }}
      >
        <Divider
          sx={{ width: "1px", height: "40px", margin: "20px 0" }}
          orientation="vertical"
          flexItem
        />

        <SearchInput
          value={searchVal}
          placeholder="Search by Schedule, Name"
          onChange={handleSearchChange}
        />

        {showEditGeneral && (
          <SolidButton
            startIcon={<AddIcon />}
            width="240px"
            height="32px"
            onClick={handleCreate}
          >
            Create Schedule Profile
          </SolidButton>
        )}
      </Box>

      <TableField
        {...table}
        pendingChildren={isLoading && <Box />}
      />

      <PreviewPopover
        key={previewProfile?.id || "schedule-preview"}
        anchorPosition={previewAnchorPosition}
        profile={previewProfile}
        popoverRef={previewPopoverRef}
        onPointerEnter={clearClosePreviewTimer}
        onPointerLeave={scheduleClosePreview}
      />

      <ScheduleModal
        open={actionState === scheduleActionState.create || actionState === scheduleActionState.edit}
        mode={actionState}
        profile={selectedProfile}
        showEditGeneral={showEditGeneral}
        onClose={closeScheduleModal}
        onSubmit={handleSubmitSchedule}
      />

      <RemoveModal
        open={actionState === scheduleActionState.remove}
        profile={selectedProfile}
        onCancel={closeScheduleModal}
        onConfirm={handleConfirmRemove}
      />

      <GlobalAlert
        open={alertInfo.open}
        severity={alertInfo.severity}
        message={alertInfo.message}
        onClose={() => setAlertInfo((prev) => ({ ...prev, open: false }))}
      />
    </Box>
  );
};

export default ProfileSchedule;
